import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { Avatar, Modal, Popover, Badge, Dropdown, Menu, Space, Drawer, Button, List } from 'antd';
import { DownOutlined, BellOutlined, UserOutlined, MenuOutlined, LeftOutlined, RightOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useRef } from 'react';
import ThemeToggle from '@/components/ThemeToggle';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationComponent from '@/pages/InterviewNotifications';
import photo from '@/style/images/photo.png';
import { API_BASE_URL, BASE_URL } from '@/config/serverApiConfig';
import { Link, useLocation, useHistory } from 'react-router-dom';
import axios from 'axios';
import { logout } from '@/redux/auth/actions';
import { usePermission } from '@/customHooks/usePermission';

const TopNav = () => {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const { pathname } = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const isDarkMode = useSelector(state => state.theme.isDarkMode);
    const {
        notifications, unreadCount, tabCounts, activeType,
        handleTabChange, markAsRead, markAllAsRead, fetchNotifications,
        fetchCounts, deleteNotification, loadMore, isLoading, hasMore
    } = useNotifications();

    const [leaveCount, setLeaveCount] = useState(0);
    const [reviewAppraisalCount, setReviewAppraisalCount] = useState(0);
    const [selfAppraisalCount, setSelfAppraisalCount] = useState(0);
    const [logoutModalVisible, setLogoutModalVisible] = useState(false);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const scrollContainerRef = useRef(null);

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 10);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            // Initial check
            handleScroll();
            // Check on resize
            window.addEventListener('resize', handleScroll);
            return () => {
                container.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', handleScroll);
            };
        }
    }, [currentAdmin]);

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const scrollAmount = 200;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        const fetchCounts = async () => {
            try {
                const [leaveRes, appraisalRes] = await Promise.all([
                    axios.get('/get-leave-request'),
                    axios.get('/get-appraisal-counts').catch(() => ({ data: { success: false } }))
                ]);
                if (leaveRes.data?.success) setLeaveCount(leaveRes.data.count);
                if (appraisalRes.data?.success) {
                    setReviewAppraisalCount(appraisalRes.data.reviewCount || 0);
                    setSelfAppraisalCount(appraisalRes.data.selfCount || 0);
                }
            } catch (err) {
                console.error('Failed to fetch counts:', err);
            }
        };
        fetchCounts();
    }, []);

    const hasAccess = (permission) => {
        if (!currentAdmin?.permissions) return false;
        return !!currentAdmin.permissions[permission];
    };

    const handleLogout = () => {
        dispatch(logout());
        history.push('/login');
    };

    const { canCreate, canEdit, canExport } = usePermission('MyProfile');

    const settingsOptions = [
        (canCreate && canEdit && canExport) && {
            key: 'roles-permissions',
            title: 'Roles & Permissions',
            icon: <SafetyCertificateOutlined />,
            path: '/roles-permissions',
            description: 'Manage user roles and permissions',
        },
    ].filter(Boolean);

    const handleSettingClick = (path) => {
        setSettingsModalOpen(false);
        history.push(path);
    };

    const navItemClass = (path) => `
        px-3 py-1.5 text-[11px] font-bold transition-all border-b-2 
        ${pathname === path
            ? 'text-[var(--color-primary)] border-[var(--color-primary)]'
            : 'text-slate-500 hover:text-[var(--color-primary)] border-transparent'
        }
    `;

    const dropdownOverlay = (items) => (
        <Menu className="mt-2 shadow-xl border border-slate-100 dark:border-slate-800 rounded-lg overflow-hidden min-w-[200px]">
            {items.map((item, idx) => (
                item.condition === false ? null : (
                    <Menu.Item key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                        {item.onClick ? (
                            <button onClick={item.onClick} className="text-[11px] font-bold py-1 flex justify-between items-center w-full bg-transparent border-none cursor-pointer">
                                {item.label}
                                {item.badge > 0 && <Badge count={item.badge} size="small" style={{ backgroundColor: '#1677FF' }} />}
                            </button>
                        ) : (
                            <Link to={item.path} className="text-[11px] font-bold py-1 flex justify-between items-center w-full">
                                {item.label}
                                {item.badge > 0 && <Badge count={item.badge} size="small" style={{ backgroundColor: '#1677FF' }} />}
                            </Link>
                        )}
                    </Menu.Item>
                )
            ))}
        </Menu>
    );

    const isHR = ['super_admin', 'Admin', 'hr_admin'].includes(currentAdmin?.role);

    const taskItems = [
        { label: 'My Daily Tasks', path: '/client-task-log' },
        { label: 'My Extra Hours', path: '/extra-hours-logs', condition: currentAdmin?.role === 'super_admin' || currentAdmin?.role === 'Admin' },
        { label: 'Extra Hours', path: '/extraHours' },
    ];

    const attendanceItems = [
        { label: 'Daily Activity Log', path: '/daily-activity-log', condition: hasAccess('AttendanceLog') },
        { label: 'Time-in / Time-out Records', path: '/attendance-report', condition: currentAdmin?.role === 'super_admin' },
        { label: 'Overtime & Deductions', path: '/overtime-extrahours', condition: currentAdmin?.role === 'super_admin' },
        { label: 'Leave Requests', path: '/LeaveRequestList', badge: leaveCount },
        { label: 'Leave Benefit Requests', path: '/leavebenefitrequests', condition: currentAdmin?.role === 'super_admin' },
    ];

    const communicationItems = [
        { label: 'Internal Communication', path: '/email' },
        { label: 'Announcements', path: '/announcement-feeds' },
        { label: 'Events & Reminders', path: '/events-reminders' },
        { label: 'Feedback & Complaints', path: '/feedback' },
        { label: 'Employee Handbook', path: '/employee-handbook' },
    ];

    const performanceItems = [
        { label: 'Activity Scorecard', path: '/activity-scorecard', condition: currentAdmin?.role === 'super_admin' },
        { label: 'Appraisal Requests', path: '/reviewappraisals', badge: reviewAppraisalCount, condition: currentAdmin?.role === 'super_admin' || currentAdmin?.role === 'Admin' },
        { label: 'Appraisal History', path: '/appraisalhistory' },
    ];

    const financeItems = [
        // { label: 'Recent Payslips', path: '/payrolls' },
        { label: 'Revenue / Invoices', path: '/maintenance', condition: currentAdmin?.role === 'super_admin' },
        { label: currentAdmin?.role === 'Employee' ? 'My Payslips' : 'Salary Management', path: '/salary-management', condition: hasAccess('Salarymanagement') || currentAdmin?.role === 'Employee' },
    ];


    const hrItems = [
        { label: 'Recruitment Panel', path: '/recruitment' },
        { label: 'Employee Management', path: '/employees' },
        { label: 'Organizational Hierarchy', path: '/organizational-hierarchy' },
        { label: 'Salary Management', path: '/salary-management' },
        { label: 'Open Positions', path: '/hire-req' },
        { label: 'Review Appraisals', path: '/reviewappraisals' },

    ];

    const teamItems = [
        { label: 'Organizational Hierarchy', path: '/organizational-hierarchy' },
    ];

    const marketingItems = [
        { label: 'Color Picker', path: '/colorpicker' },
        { label: 'Meta Length Checker', path: '/seo-snippet' },
        { label: 'AI Text Detector', path: '/ai-text-detector' },
        { label: 'Page Speed Insights', path: '/page-speed-insights' },
        { label: 'Text to Speech', path: '/voice-over' },
        { label: 'Google Trends', path: '/google-trends' },
        { label: 'Google Keywords Planner', path: '/google-keywords-planner' },
        { label: 'Google Ads Generator', path: '/google-ads' },
    ];

    const allNavCategories = [
        { label: 'Dashboard', path: '/', isDashboard: true },
        { label: 'Tasks', items: taskItems, activePaths: ['task', 'Hours'] },
        // { label: 'Team', items: teamItems, condition: !isHR, activePaths: ['hierarchy'] },
        { label: 'Attendance', items: attendanceItems, activePaths: ['attendance', 'Leave'] },
        { label: 'Communication', items: communicationItems, activePaths: ['email', 'complaint', 'announcement', 'handbook', 'events-reminders'] },
        { label: 'Performance', items: performanceItems, activePaths: ['appraisal', 'scorecard'] },
        { label: 'Finance', items: financeItems, activePaths: ['salary', 'payrolls', 'invoice'] },
        { label: 'Marketing', items: marketingItems, condition: currentAdmin?.department === 'Marketing' || currentAdmin?.email === 'admin@dymaxtech.com', activePaths: ['colorpicker', 'seo-snippet', 'ai-text-detector', 'page-speed-insights', 'voice-over', 'google-trends', 'google-keywords-planner', 'google-ads'] },
        { label: 'Human Resources', items: hrItems, condition: isHR, activePaths: ['employees', 'recruitment', 'hierarchy'] },
    ];

    return (
        <nav className="h-14 bg-white dark:bg-[#000000] border-b border-slate-200 dark:border-slate-800 px-12 sm:px-16 md:px-24 lg:px-32 xl:px-36 flex items-center justify-between sticky top-0 z-[1000]">
            {/* Left: Branding */}
            <div className="flex-shrink-0 mr-4">
                <Link to="/" className="flex items-center group">
                    <img src="dymaxtech-logo-transparent.png" alt="DymaxTech" className="h-8 w-auto transition-transform group-hover:scale-105" />
                </Link>
            </div>

            {/* Middle: Primary Nav - Scrollable with Arrows */}
            <div className="relative flex-1 flex items-center overflow-hidden h-full mx-2">
                {showLeftArrow && (
                    <button
                        onClick={() => scroll('left')}
                        className="absolute left-0 z-10 h-8 w-8 flex items-center justify-center bg-white/90 dark:bg-black/90 shadow-md rounded-full border border-slate-200 dark:border-slate-800 text-slate-500 hover:text-[var(--color-primary)] transition-all"
                    >
                        <LeftOutlined style={{ fontSize: '10px' }} />
                    </button>
                )}

                <div
                    ref={scrollContainerRef}
                    className="flex-1 flex items-center gap-0.4 sm:gap-0.2 overflow-x-auto no-scrollbar scroll-smooth whitespace-nowrap lg:justify-center px-4"
                >
                    {allNavCategories.map((cat, idx) => {
                        if (cat.condition === false) return null;
                        if (cat.isDashboard) {
                            return <Link key={idx} to={cat.path} className={`${navItemClass(cat.path)} flex-shrink-0`}>{cat.label?.toUpperCase()}</Link>;
                        }
                        return (
                            <Dropdown key={idx} overlay={dropdownOverlay(cat.items)} trigger={['hover', 'click']}>
                                <button className={`px-2 sm:px-1.5 py-1.5 text-[11px] sm:text-[10px] font-bold flex items-center gap-1 uppercase transition-all flex-shrink-0 ${cat.activePaths.some(p => pathname.includes(p)) ? 'text-[var(--color-primary)]' : 'text-slate-500 hover:text-[var(--color-primary)]'}`}>
                                    {cat.label} <DownOutlined className="text-[9px]" />
                                </button>
                            </Dropdown>
                        );
                    })}
                </div>

                {showRightArrow && (
                    <button
                        onClick={() => scroll('right')}
                        className="absolute right-0 z-10 h-8 w-8 flex items-center justify-center bg-white/90 dark:bg-black/90 shadow-md rounded-full border border-slate-200 dark:border-slate-800 text-slate-500 hover:text-[var(--color-primary)] transition-all"
                    >
                        <RightOutlined style={{ fontSize: '10px' }} />
                    </button>
                )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-4 flex-shrink-0">
                <ThemeToggle />

                <Popover
                    content={
                        <NotificationComponent
                            notifications={notifications || []}
                            tabCounts={tabCounts || { all: 0 }}
                            activeType={activeType}
                            handleTabChange={handleTabChange}
                            markAsRead={markAsRead}
                            markAllAsRead={markAllAsRead}
                            fetchNotifications={fetchNotifications}
                            fetchCounts={fetchCounts}
                            deleteNotification={deleteNotification}
                            isNotificationLoading={isLoading}
                            loadMore={loadMore}
                            hasMore={hasMore}
                        />
                    }
                    title={<span className="text-xs font-bold uppercase tracking-tight p-2 block border-b dark:border-slate-800">Notifications</span>}
                    trigger="click"
                    transitionName=""
                >
                    <button className="relative p-2 text-slate-500 hover:text-[var(--color-primary)] transition-all bg-slate-50 dark:bg-slate-900 rounded-full border border-slate-200 dark:border-slate-800 focus:outline-none">
                        <BellOutlined style={{ fontSize: '18px' }} />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white dark:border-slate-900 shadow-sm leading-none whitespace-nowrap">
                                {unreadCount}
                            </span>
                        )}
                    </button>
                </Popover>

                <Dropdown
                    overlay={
                        <Menu className="mt-2 min-w-[220px] shadow-2xl border border-slate-100 dark:border-slate-800 p-2 rounded-xl">
                            <div className="px-4 py-3 border-b border-slate-50 dark:border-slate-800 mb-2">
                                <p className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-tight">{currentAdmin?.name} {currentAdmin?.surname}</p>
                                <p className="text-[10px] text-slate-400 font-bold truncate lowercase">{currentAdmin?.email || 'email@example.com'}</p>
                            </div>
                            <Menu.Item key="profile">
                                <Link to="/my-profile" className="text-[11px] font-bold">My Profile</Link>
                            </Menu.Item>
                            <Menu.Item key="handbook">
                                <a href="/employee-handbook" target="_blank" rel="noopener noreferrer" className="text-[11px] font-bold">Employee Handbook</a>
                            </Menu.Item>
                            <Menu.Item key="scorecard">
                                <Link to="/activity-scorecard" className="text-[11px] font-bold">View Scorecard</Link>
                            </Menu.Item>
                            {settingsOptions.length > 0 && (
                                <Menu.Item key="settings" onClick={() => setSettingsModalOpen(true)}>
                                    <span className="text-[11px] font-bold">Settings</span>
                                </Menu.Item>
                            )}
                            <Menu.Divider />
                            <Menu.Item key="logout" danger onClick={() => setLogoutModalVisible(true)}>
                                <span className="text-[11px] font-black uppercase">Log Out</span>
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <button className="flex items-center gap-2 pl-3 pr-1 py-1 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-800 transition-all focus:outline-none">
                        <Space>
                            <span className="text-[10px] font-black tracking-tight text-slate-900 dark:text-white hidden sm:inline uppercase">
                                {currentAdmin?.name} {currentAdmin?.surname}
                            </span>
                            <Avatar
                                src={currentAdmin?.photo ? `${BASE_URL}public/uploads/admin/${currentAdmin.photo}` : null}
                                size={28}
                                className="border border-slate-200 dark:border-slate-700 shadow-sm"
                            >
                                {!currentAdmin?.photo && (currentAdmin?.name?.charAt(0) || '') + (currentAdmin?.surname?.charAt(0) || '')}
                            </Avatar>
                        </Space>
                    </button>
                </Dropdown>
            </div>

            <Modal
                title={<span className="text-sm font-black uppercase tracking-tight">System Logout</span>}
                open={logoutModalVisible}
                onOk={handleLogout}
                onCancel={() => setLogoutModalVisible(false)}
                okText="LOG OUT"
                cancelText="CANCEL"
                centered
                okButtonProps={{ danger: true, className: "font-black" }}
                cancelButtonProps={{ className: "font-black" }}
            >
                <p className="text-xs text-slate-500 font-medium">Are you sure you want to end your current session?</p>
            </Modal>

            {/* Settings Modal */}
            <Modal
                title="Settings"
                open={settingsModalOpen}
                onCancel={() => setSettingsModalOpen(false)}
                footer={null}
                width={500}
            >
                <List
                    itemLayout="horizontal"
                    dataSource={settingsOptions}
                    renderItem={(item) => (
                        <List.Item
                            style={{ cursor: 'pointer', padding: '12px' }}
                            onClick={() => handleSettingClick(item.path)}
                            className={`settings-list-item ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50'}`}
                        >
                            <List.Item.Meta
                                avatar={<span style={{ fontSize: '20px' }}>{item.icon}</span>}
                                title={<span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.title}</span>}
                                description={<span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>{item.description}</span>}
                            />
                        </List.Item>
                    )}
                />
            </Modal>
        </nav>
    );
};

export default TopNav;
